import { useRef, useEffect, forwardRef, useImperativeHandle, useState } from 'react';
import SignatureCanvas from 'react-signature-canvas';
import { Button } from '@/components/ui/button';
import { RotateCcw } from 'lucide-react';

interface SignaturePadProps {
    value?: string;
    onChange?: (signature: string) => void;
    onClear?: () => void;
    width?: number;
    height?: number;
    disabled?: boolean;
    backgroundColor?: string;
    penColor?: string;
    className?: string;
    /** Contract paper: one overlay line, no extra hint under the pad. */
    compact?: boolean;
    /** Canvas width tracks the parent so the pad never overflows a narrow column. */
    fluid?: boolean;
    /**
     * Do not capture touch until the user taps the pad. Keeps the surrounding
     * page scrollable (react-signature-canvas sets touch-action: none).
     */
    armToSign?: boolean;
}

export interface SignaturePadRef {
    clear: () => void;
    isEmpty: () => boolean;
    getTrimmedCanvas: () => HTMLCanvasElement;
    getSignatureData: () => string;
}

const SignaturePad = forwardRef<SignaturePadRef, SignaturePadProps>(({
    value,
    onChange,
    onClear,
    width = 400,
    height = 200,
    disabled = false,
    backgroundColor = 'rgba(255,255,255,0)',
    penColor = 'black',
    className = '',
    compact = false,
    fluid = false,
    armToSign,
}, ref) => {
    const sigPadRef = useRef<SignatureCanvas>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const [isDrawing, setIsDrawing] = useState(false);
    const [fluidWidth, setFluidWidth] = useState(0);
    const [armed, setArmed] = useState(false);
    const lastLoadedValue = useRef<string>('');
    const shouldArm = armToSign ?? fluid;
    const canvasWidth = fluid ? fluidWidth : width;
    const captureStrokes = !disabled && (!shouldArm || armed);

    useImperativeHandle(ref, () => ({
        clear: () => {
            sigPadRef.current?.clear();
            onChange?.('');
            onClear?.();
        },
        isEmpty: () => {
            return sigPadRef.current?.isEmpty() || false;
        },
        getTrimmedCanvas: () => {
            try {
                return sigPadRef.current?.getTrimmedCanvas?.() || sigPadRef.current?.getCanvas() || document.createElement('canvas');
            } catch (error) {
                console.error('Error getting trimmed canvas:', error);
                return sigPadRef.current?.getCanvas() || document.createElement('canvas');
            }
        },
        getSignatureData: () => {
            return sigPadRef.current?.toDataURL() || '';
        }
    }));

    useEffect(() => {
        if (!fluid) return;
        const el = containerRef.current;
        if (!el) return;

        const update = () => {
            const next = Math.max(0, Math.floor(el.clientWidth));
            setFluidWidth((prev) => (prev === next ? prev : next));
        };
        update();

        if (typeof ResizeObserver === 'undefined') {
            window.addEventListener('resize', update);
            return () => window.removeEventListener('resize', update);
        }

        const observer = new ResizeObserver(update);
        observer.observe(el);
        return () => observer.disconnect();
    }, [fluid]);

    useEffect(() => {
        // Uncontrolled (no `value` prop): do not sync from parent. The old
        // `!value → clear()` path ran on every isDrawing flip and wiped the
        // stroke / swallowed onEnd — Confirm & Sign stayed disabled (SHP-2).
        if (value === undefined) return;
        if (isDrawing) return;

        if (value && sigPadRef.current && value !== lastLoadedValue.current) {
            try {
                sigPadRef.current.clear();
                sigPadRef.current.fromDataURL(value);
                lastLoadedValue.current = value;
            } catch (error) {
                console.error('Error loading signature:', error);
            }
        } else if (!value && sigPadRef.current) {
            sigPadRef.current.clear();
            lastLoadedValue.current = '';
        }
    }, [value, isDrawing, canvasWidth]);

    const handleBegin = () => {
        setIsDrawing(true);
    };

    const handleEnd = () => {
        setIsDrawing(false);
        if (sigPadRef.current && onChange) {
            const signatureData = sigPadRef.current.toDataURL();
            lastLoadedValue.current = signatureData;
            onChange(signatureData);
        }
    };

    const handleClear = () => {
        sigPadRef.current?.clear();
        lastLoadedValue.current = '';
        onChange?.('');
        onClear?.();
    };

    const emptyHint = shouldArm && !armed
        ? (value ? 'Tap to edit signature' : 'Tap to sign')
        : compact
            ? 'Sign here'
            : 'Draw your signature here';

    return (
        <div
            ref={containerRef}
            className={`signature-pad-container w-full max-w-full ${className}`}
            data-testid="signature-pad"
            data-armed={captureStrokes ? 'true' : 'false'}
            onClick={() => {
                if (shouldArm && !armed && !disabled) setArmed(true);
            }}
        >
            <div className="relative w-full max-w-full">
                {canvasWidth > 0 && (
                    <SignatureCanvas
                        key={`sig-${canvasWidth}x${height}`}
                        ref={sigPadRef}
                        canvasProps={{
                            width: canvasWidth,
                            height,
                            className: `block max-w-full border-2 border-dashed border-border rounded-none ${
                                disabled ? 'opacity-50 pointer-events-none' : captureStrokes ? 'cursor-crosshair hover:border-border' : 'cursor-pointer'
                            }`,
                            style: {
                                display: 'block',
                                width: canvasWidth,
                                height,
                                maxWidth: '100%',
                                touchAction: captureStrokes ? 'none' : 'pan-y',
                                pointerEvents: captureStrokes ? 'auto' : 'none',
                            },
                        }}
                        backgroundColor={backgroundColor}
                        penColor={penColor}
                        onBegin={handleBegin}
                        onEnd={handleEnd}
                        dotSize={() => 0}
                        minWidth={0.5}
                        maxWidth={2.5}
                        throttle={16}
                        minDistance={1}
                    />
                )}

                {!disabled && (!value || sigPadRef.current?.isEmpty()) && !isDrawing && (
                    <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                        <div className="text-center text-muted-foreground">
                            <p className={compact && !shouldArm ? 'text-xs' : 'text-sm'}>{emptyHint}</p>
                            {!compact && !shouldArm && (
                                <p className="text-xs mt-1">Use a mouse, stylus, or finger on a tablet</p>
                            )}
                        </div>
                    </div>
                )}
            </div>

            {shouldArm && armed && !disabled && (
                <div className="flex justify-end mt-2">
                    <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={(event) => {
                            event.stopPropagation();
                            setArmed(false);
                        }}
                    >
                        Done signing
                    </Button>
                </div>
            )}

            <div className={`flex ${compact ? 'justify-start mt-1.5' : 'justify-center gap-2 mt-3'}`}>
                <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={handleClear}
                    disabled={disabled}
                    className="flex items-center gap-2"
                >
                    <RotateCcw className="h-4 w-4" />
                    Clear
                </Button>
            </div>

            {!compact && (
                <div className="mt-2 text-xs text-muted-foreground text-center">
                    <p>Sign with your finger, stylus, or mouse.</p>
                </div>
            )}
        </div>
    );
});

SignaturePad.displayName = 'SignaturePad';

export default SignaturePad;
